/**
 * @fileoverview
 * The BlocksRuntimeCache caches data about the top block of scripts so that
 * Runtime can iterate a targeted opcode and iterate the returned set faster.
 * Many top blocks need to match fields as well as opcode, since that matching
 * compares strings in uppercase we can go ahead and uppercase the cached value
 * so we don't need to in the future.
 */

import type {VMField} from '../serialization/schema';
import type Blocks from './blocks';

/**
 * A set of cached data about the top block of a script.
 */
class RuntimeScriptCache {
    /**
     * Container with block data for blockId.
     */
    container: Blocks;

    /**
     * ID for block this instance caches.
     */
    blockId: string;

    /**
     * Formatted fields or fields of input blocks ready for comparison in
     * runtime.
     *
     * This is a clone of parts of the targeted blocks. Changes to these
     * clones are limited to copies under RuntimeScriptCache and will not
     * appear in the original blocks in their container. This copy is
     * modified changing the case of strings to uppercase. These uppercase
     * values will be compared later by the VM.
     */
    fieldsOfInputs: Record<string, VMField>;

    /**
     * @param container - Container holding the block and related data
     * @param blockId - Id for whose block data is cached in this instance
     */
    constructor (container: Blocks, blockId: string) {
        this.container = container;

        this.blockId = blockId;

        const block = container.getBlock(blockId)!;
        const fields = container.getFields(block)!;

        this.fieldsOfInputs = Object.assign({}, fields);
        if (Object.keys(fields).length === 0) {
            const inputs = container.getInputs(block);
            for (const input in inputs) {
                if (!Object.prototype.hasOwnProperty.call(inputs, input)) continue;
                const id = inputs[input].block;
                const inputBlock = container.getBlock(id!)!;
                const inputFields = container.getFields(inputBlock);
                Object.assign(this.fieldsOfInputs, inputFields);
            }
        }
        for (const key in this.fieldsOfInputs) {
            const field = this.fieldsOfInputs[key] = Object.assign({}, this.fieldsOfInputs[key]);
            if (field.value?.toUpperCase) {
                field.value = field.value.toUpperCase();
            }
        }
    }
}

/**
 * Get an array of scripts from a block container prefiltered to match opcode.
 * @param container - Container of blocks
 * @param opcode - Opcode to filter top blocks by
 * @returns Array of cached script data for scripts with the given opcode
 */
const getScripts = function (container: Blocks, opcode: string): RuntimeScriptCache[] {
    const runtimeCache = container._cache.scripts as Record<string, RuntimeScriptCache[]>;

    let scripts = runtimeCache[opcode];
    if (!scripts) {
        scripts = runtimeCache[opcode] = [];

        const allScripts = container.getScripts();
        for (let i = 0; i < allScripts.length; i++) {
            const topBlockId = allScripts[i];
            const block = container.getBlock(topBlockId);
            if (block?.opcode === opcode) {
                scripts.push(new RuntimeScriptCache(container, topBlockId));
            }
        }
    }

    return scripts;
};

export {
    getScripts,
    RuntimeScriptCache
};
